// Conversation card used on the Current Action screen. Mirrors the
// section.card.conversation structure in the approved HTML, including the
// three-channel toggle (Text, Email, Call) and the prepared draft.
//
// TODO(runtime): Send reply dispatches depend on the active channel:
//   - Text: POST /v1/actions/KIXIE_SEND_SMS (confirmed, dormant)
//   - Email: services/rep-email .send() (writes a HubSpot Engagement)
//   - Call:  services/call-evidence attaches as call notes
// Current scaffold leaves the handler as a no-op placeholder because
// LIVE_WRITE_GATE_KIXIE_SEND_SMS is still off.
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import { CURRENT_ACTION_MOCK } from '../mockData.js';
import { SARAH } from '../../../data/mockData.js';

type Channel = 'Text' | 'Email' | 'Call';

export interface ConversationCardProps {
  ticketCreated: boolean;
  ticketId: string | null;
  onOpenCsTicket: () => void;
}

export function ConversationCard(props: ConversationCardProps): ReactNode {
  const { ticketCreated, ticketId, onOpenCsTicket } = props;
  const [activeChannel, setActiveChannel] = useState<Channel>('Text');
  const [drafts, setDrafts] = useState<Record<Channel, string>>({
    Text: CURRENT_ACTION_MOCK.preparedReply,
    Email: CURRENT_ACTION_MOCK.preparedReply,
    Call: CURRENT_ACTION_MOCK.callNotes,
  });

  const inboundText =
    activeChannel === 'Call'
      ? 'Call context: Sarah reports one Gut X bottle arrived leaking.'
      : CURRENT_ACTION_MOCK.inboundMessage;
  const draftLabel = activeChannel === 'Call' ? 'Call notes' : 'Prepared reply';
  const draftHint = activeChannel === 'Call' ? 'Capture the useful details' : 'Review before sending';
  const sendLabel = activeChannel === 'Call' ? 'Save call notes' : 'Send reply';
  const channelLabel =
    activeChannel === 'Call'
      ? 'On a call with Sarah'
      : activeChannel === 'Email'
        ? 'Email'
        : 'Text message';

  function pickChannel(next: Channel): void {
    // Save current draft text before switching to preserve the typed content.
    setDrafts((prev) => ({ ...prev, [activeChannel]: prev[activeChannel] }));
    setActiveChannel(next);
  }

  function updateDraft(value: string): void {
    setDrafts((prev) => ({ ...prev, [activeChannel]: value }));
  }

  const needStrong = ticketCreated
    ? 'Sales handoff complete.'
    : 'Keep the conversation moving.';
  const needBody = ticketCreated
    ? 'Click Finish and Next when you are ready to leave this conversation.'
    : 'Once the ticket is confirmed, Customer Service takes it from here.';

  return (
    <section className="card conversation" aria-label="Current conversation">
      <div className="card-head">
        <div className="row">
          <span className="head-icon">
            <Icon id="message" />
          </span>
          <h3>Conversation with {SARAH.firstName}</h3>
        </div>
        <div className="channel-preview">
          <span>Conversation channel</span>
          <div role="group" aria-label="Preview conversation channel">
            {(['Text', 'Email', 'Call'] as Channel[]).map((channel) => (
              <button
                key={channel}
                type="button"
                aria-pressed={activeChannel === channel}
                onClick={() => pickChannel(channel)}
              >
                {channel}
              </button>
            ))}
          </div>
        </div>
        <div className="conversation-header-actions">
          <span className="small" id="channel-label">
            {channelLabel}
          </span>
          <button
            type="button"
            className="cs-inline"
            id="get-cs-help"
            disabled={ticketCreated}
            onClick={onOpenCsTicket}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 13v-1a8 8 0 0 1 16 0v1M4 12H3v7h4v-7H4Zm16 0h1v7h-4v-7h3ZM20 19c0 2-3 3-7 3" />
            </svg>
            {ticketCreated ? '✓ Help Desk ticket created' : 'Create CS Ticket'}
          </button>
        </div>
      </div>
      <div className="thread">
        <div className="day-marker">TODAY</div>
        <div className="message-meta">
          <strong>{SARAH.fullName}</strong>
          <span>{CURRENT_ACTION_MOCK.inboundTime}</span>
        </div>
        <div className="bubble" id="inbound-context">
          {inboundText}
        </div>
      </div>
      <div className="draft">
        <div className="draft-top">
          <strong id="draft-label">{draftLabel}</strong>
          <span id="draft-hint">{draftHint}</span>
        </div>
        <textarea
          id="draft"
          aria-label="Conversation draft or call notes"
          value={drafts[activeChannel]}
          onChange={(e) => updateDraft(e.target.value)}
        />
        <div className="draft-bottom">
          <span className="draft-helper">Click the prepared reply to edit.</span>
          <button
            id="send"
            className="primary"
            type="button"
            onClick={() => {
              // TODO(runtime): dispatch to KIXIE_SEND_SMS / rep-email /
              // call-evidence depending on activeChannel once gates are
              // flipped on.
            }}
          >
            <Icon id="icon-send" />
            <span id="send-label">{sendLabel}</span>
          </button>
        </div>
      </div>
      {ticketCreated && ticketId ? (
        <div className="cs-inline-confirmation" id="inline-confirmation" role="status">
          <strong>{`✓ Help Desk ticket ${ticketId} created · Preview`}</strong>
          <p>Sent to Customer Service. History note recorded. No Sales follow-up needed.</p>
          <div className="ticket-history-note">
            <strong>HISTORY · JUST NOW</strong>
            <p>
              Jamie Dorsey clicked Confirm. System confirmed ticket {ticketId} was recorded and
              routed to Customer Service. Source: {activeChannel}. Related order: {CURRENT_ACTION_MOCK.orderId}.
            </p>
            <p>Issue: {CURRENT_ACTION_MOCK.ticketIssue}</p>
          </div>
          <small>Local design preview only. No real ticket or customer message was sent.</small>
        </div>
      ) : null}
      <div className="need">
        <Icon id="check" />
        <div>
          <strong>{needStrong}</strong>
          <p>{needBody}</p>
        </div>
      </div>
    </section>
  );
}
